import { useEffect } from 'react'
import { create } from 'zustand'

// ── Toast Store ──────────────────────────────────────────────────────────────

type ToastType = 'success' | 'error' | 'info'

interface Toast {
  id: number
  message: string
  type: ToastType
}

interface ToastState {
  toasts: Toast[]
  addToast: (message: string, type?: ToastType) => void
  removeToast: (id: number) => void
}

let nextId = 0

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  addToast: (message, type = 'info') => {
    const id = nextId++
    set((s) => ({ toasts: [...s.toasts, { id, message, type }] }))
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }))
    }, 4000)
  },
  removeToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}))

// ── Toast Container ──────────────────────────────────────────────────────────

const typeStyles: Record<ToastType, string> = {
  success: 'bg-[var(--color-success)] text-white',
  error: 'bg-[var(--color-error)] text-white',
  info: 'bg-[var(--color-surface)] text-[var(--color-text)] border border-[var(--color-border)]',
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  useEffect(() => {
    const el = document.getElementById(`toast-${toast.id}`)
    if (el) {
      requestAnimationFrame(() => el.classList.add('translate-x-0', 'opacity-100'))
    }
  }, [toast.id])

  return (
    <div
      id={`toast-${toast.id}`}
      className={`translate-x-full opacity-0 transition-all duration-300 ease-out rounded-lg px-4 py-3 text-sm font-medium shadow-lg cursor-pointer ${typeStyles[toast.type]}`}
      onClick={onDismiss}
    >
      {toast.message}
    </div>
  )
}

export function ToastContainer() {
  const { toasts, removeToast } = useToastStore()

  if (toasts.length === 0) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => removeToast(t.id)} />
      ))}
    </div>
  )
}
